import { useState } from 'react'
import TaskList from './TaskList'
import { decomposeTask } from '@/utils/decomposeTask'

const DEFAULT_SUBTASKS = [
  { title: 'Research and gather sources', timeEstimate: 30 },
  { title: 'Create outline', timeEstimate: 15 },
  { title: 'Write first draft', timeEstimate: 45 },
  { title: 'Review and revise', timeEstimate: 20 },
  { title: 'Final check and submit', timeEstimate: 10 },
]

function TaskDecomposition({ tasks = [], setTasks, expandTaskId, onClearExpandTask, onToggleSubtask, onDeleteTask, onUpdateTask, onUpdateSubtask, onAddSubtask, onDeleteSubtask, onReorderSubtasks, onReplaceSubtasks, onStartFocus }) {
  const [taskInput, setTaskInput] = useState('')
  const [description, setDescription] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [priority, setPriority] = useState('medium')
  const [size, setSize] = useState('medium')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [formOpen, setFormOpen] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    if (!taskInput.trim() || isLoading) return

    const taskTitle = taskInput.trim()
    const fallback = new Date(Date.now() + 7 * 86400000)
    const taskDueDate = dueDate || `${fallback.getFullYear()}-${String(fallback.getMonth() + 1).padStart(2, '0')}-${String(fallback.getDate()).padStart(2, '0')}`
    const taskId = crypto.randomUUID()

    setIsLoading(true)
    setError(null)

    let subtaskData

    try {
      subtaskData = await decomposeTask({
        title: taskTitle,
        description: description.trim(),
        dueDate: taskDueDate,
        priority,
        size,
      })
    } catch (err) {
      console.warn('AI decomposition failed, using defaults:', err.message)
      subtaskData = DEFAULT_SUBTASKS
      setError('AI unavailable — used default steps instead.')
    } finally {
      setIsLoading(false)
    }

    const newTask = {
      id: taskId,
      title: taskTitle,
      dueDate: taskDueDate,
      priority,
      size,
      subtasks: subtaskData.map((st, i) => ({
        id: `${taskId}-${i}`,
        title: st.title,
        timeEstimate: st.timeEstimate,
        done: false,
      })),
    }

    setTasks((prev) => [...prev, newTask])
    setTaskInput('')
    setDescription('')
    setDueDate('')
    setPriority('medium')
    setSize('medium')
    setFormOpen(false)
  }

  return (
    <div className="page task-decomposition">
      <div className="collapsible-section">
        <button
          type="button"
          className="collapsible-header"
          onClick={() => setFormOpen((prev) => !prev)}
        >
          <h2 className="page-title">New Task</h2>
          <span className={`collapsible-chevron${formOpen ? ' collapsible-chevron--open' : ''}`}>
            &#9662;
          </span>
        </button>

        {formOpen && (
          <div className="collapsible-body">
            <p className="page-description">
              Describe your assignment and we'll break it into small, manageable steps
              with time estimates.
            </p>

            <form className="task-form" onSubmit={handleSubmit}>
              <div className="form-group form-group--full">
                <label className="form-label">Assignment</label>
                <input
                  type="text"
                  className="task-input"
                  placeholder='Try "Write a 5-page history paper" or "Study for Chemistry exam"'
                  value={taskInput}
                  onChange={(e) => setTaskInput(e.target.value)}
                  disabled={isLoading}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Due Date</label>
                <input
                  type="date"
                  className="task-input task-date-input"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  disabled={isLoading}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select
                  className="task-input task-priority-input"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Size</label>
                <select
                  className="task-input task-size-input"
                  value={size}
                  onChange={(e) => setSize(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="large">Large</option>
                  <option value="medium">Medium</option>
                  <option value="small">Small</option>
                </select>
              </div>
              <div className="form-group form-group--full">
                <label className="form-label">Details</label>
                <textarea
                  className="task-input task-description-input"
                  placeholder="Add more details — requirements, page count, topics to cover, professor's instructions..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={isLoading}
                  rows={3}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? 'Breaking it down...' : 'Break It Down'}
              </button>
            </form>

            {isLoading && (
              <div className="loading-indicator">
                <div className="loading-spinner" />
                <p className="loading-text">
                  AI is analyzing your assignment and creating personalized steps...
                </p>
              </div>
            )}

            {error && (
              <div className="decompose-error">
                <p>{error}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <TaskList
        tasks={tasks}
        expandTaskId={expandTaskId}
        onClearExpandTask={onClearExpandTask}
        onToggleSubtask={onToggleSubtask}
        onDeleteTask={onDeleteTask}
        onUpdateTask={onUpdateTask}
        onUpdateSubtask={onUpdateSubtask}
        onAddSubtask={onAddSubtask}
        onDeleteSubtask={onDeleteSubtask}
        onReorderSubtasks={onReorderSubtasks}
        onReplaceSubtasks={onReplaceSubtasks}
        onStartFocus={onStartFocus}
      />
    </div>
  )
}

export default TaskDecomposition
